import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@envs/environment';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

const cache: Map<string, HttpResponse<any>> = new Map();

@Injectable({ providedIn: 'root' })
export class SfCacheInterceptor implements HttpInterceptor {
  private cacheTimeInMinutes = 5;

  constructor(private readonly tm: GoogleTagManagerService) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip for GM API due to a number of async data updates
    if (req.url.includes('/api/gm/')) {
      return next.handle(req);
    }
    if (req.method === 'POST' && environment.useGoogleAnalytics) {
      this.tm.pushTag({
        event: 'cloudia.form_submission',
        endpoint_url: req.url,
        data: req.body,
      });
    }
    if (
      req.method === 'POST' ||
      req.method === 'PATCH' ||
      req.method === 'PUT'
    ) {
      const apiName = req.url
        .slice(req.url.indexOf('/api/') + 5)
        .substring(0, req.url.indexOf('/') - 1);
      const keys = Array.from(cache.keys()).filter((key) =>
        key.includes('/' + apiName)
      );
      keys.forEach((key) => cache.delete(key));
    }
    if (req.method === 'DELETE') {
      cache.clear();
    }
    if (req.method !== 'GET') {
      return next.handle(req).pipe(
        tap((httpEvent) => {
          if (
            httpEvent instanceof HttpResponse &&
            req.method === 'POST' &&
            environment.useGoogleAnalytics
          ) {
            this.tm.pushTag({
              event: 'cloudia.form_backend_response',
              endpoint_url: req.url,
              data: httpEvent.body,
            });
          }
        })
      );
    }
    if (req.headers.get('reset')) {
      cache.delete(req.urlWithParams);
    }
    const cachedResponse: HttpResponse<any> = cache.get(req.urlWithParams);
    if (cachedResponse) {
      return of(cachedResponse.clone());
    } else {
      return next.handle(req).pipe(
        tap((stateEvent) => {
          if (stateEvent instanceof HttpResponse) {
            cache.set(req.urlWithParams, stateEvent.clone());
          }
          setTimeout(() => {
            cache.delete(req.urlWithParams);
          }, 1000 * 60 * this.cacheTimeInMinutes);
        })
      );
    }
  }

  public clearCache() {
    cache.clear();
  }

  public clearByBasePath(basePath: string | string[]) {
    if (!Array.isArray(basePath)) {
      basePath = [basePath];
    }
    const keys = [...cache.keys()];

    basePath.forEach((path) => {
      const onlyMatching = keys.filter((key) => key.includes(path));
      onlyMatching.forEach((key) => cache.delete(key));
    });
  }
}
